import { Data, Match } from 'effect';
import type { Layout, LayoutItem } from 'react-grid-layout';
import { planContinuationDrop } from './continuationDrop';
import {
  insertBlockIntoPage,
  packLayoutByReadingOrder,
  reorderBlockWithinPage,
  sortLayoutByReadingOrder,
} from './sourcePageLayout';

export const fitsAboveExpandedBlock = ({
  destLayout,
  expandedBlockId,
  movedItem,
  atIndex,
  noColumns,
  expandedMinRows,
  maxUsableRows,
}: {
  destLayout: Layout;
  expandedBlockId: string;
  movedItem: LayoutItem;
  atIndex: number;
  noColumns: number;
  expandedMinRows: number;
  maxUsableRows: number;
}): boolean => {
  const simulated = insertBlockIntoPage({ layout: destLayout, item: movedItem, atIndex, noColumns });
  const newExpanded = simulated.find((l) => l.i === expandedBlockId);
  return newExpanded !== undefined && newExpanded.y + expandedMinRows <= maxUsableRows;
};

export const sourcePageOccupiedBottom = ({
  layout,
  expandedBlockId,
  expandedRows,
}: {
  layout: Layout;
  expandedBlockId: string;
  expandedRows: number;
}): number => layout.reduce((max, l) => Math.max(max, l.i === expandedBlockId ? l.y + expandedRows : l.y + l.h), 0);

export type OutlineDropDestinationKind = 'first-slice' | 'middle' | 'last' | 'single-expanded';

export type OutlineDropPlan = Data.TaggedEnum<{
  Revert: {};
  InsertIntoPage: { readonly atIndex: number };
  AppendToContinuation: { readonly sliceLayoutId: string; readonly userBlocks: Layout };
}>;

export const OutlineDropPlan = Data.taggedEnum<OutlineDropPlan>();

export interface PlanOutlineExpandedDropParams {
  readonly destinationKind: OutlineDropDestinationKind;
  readonly isAbove: boolean;
  readonly atIndex: number;
  readonly maxUsableRows: number;
  readonly destLayout: Layout;
  readonly movedItem: LayoutItem;
  readonly noColumns: number;
  readonly expandedMinRows: number;
  readonly expandedRows: number;
  readonly expandedBlockId: string;
  readonly existingUserBlocks: Layout;
  readonly sliceRowCount: number;
  readonly noRows: number;
  readonly sliceLayoutId: string;
}

/**
 * Pure routing decision for a block dropped in the Outline panel onto a page occupied by an
 * expanded multi-page block. Mirrors the canvas guards (`SourceGridPageView.onDrop` /
 * `ContinuationGridPageView.onDrop`) so the Outline never accepts a drop the canvas itself
 * would reject.
 */
export const planOutlineExpandedDrop = ({
  destinationKind,
  isAbove,
  atIndex,
  maxUsableRows,
  destLayout,
  movedItem,
  noColumns,
  expandedMinRows,
  expandedRows,
  expandedBlockId,
  existingUserBlocks,
  sliceRowCount,
  noRows,
  sliceLayoutId,
}: PlanOutlineExpandedDropParams): OutlineDropPlan => {
  return Match.value(destinationKind).pipe(
    Match.when('middle', () => OutlineDropPlan.Revert()),
    Match.when('first-slice', () =>
      isAbove &&
      fitsAboveExpandedBlock({
        destLayout,
        expandedBlockId,
        movedItem,
        atIndex,
        noColumns,
        expandedMinRows,
        maxUsableRows,
      })
        ? OutlineDropPlan.InsertIntoPage({ atIndex })
        : OutlineDropPlan.Revert(),
    ),
    Match.when('last', () => {
      const { fitsOnContinuation, userBlocks } = planContinuationDrop({
        existingUserBlocks,
        movedItem,
        sliceRowCount,
        pageRows: noRows,
        noColumns,
      });
      return fitsOnContinuation
        ? OutlineDropPlan.AppendToContinuation({ sliceLayoutId, userBlocks })
        : OutlineDropPlan.Revert();
    }),
    Match.when('single-expanded', () => {
      if (isAbove) {
        const fitsAbove = fitsAboveExpandedBlock({
          destLayout,
          expandedBlockId,
          movedItem,
          atIndex,
          noColumns,
          expandedMinRows,
          maxUsableRows,
        });
        return fitsAbove ? OutlineDropPlan.InsertIntoPage({ atIndex }) : OutlineDropPlan.Revert();
      }
      const occupiedBottom = sourcePageOccupiedBottom({ layout: destLayout, expandedBlockId, expandedRows });
      return occupiedBottom + movedItem.h <= maxUsableRows
        ? OutlineDropPlan.InsertIntoPage({ atIndex })
        : OutlineDropPlan.Revert();
    }),
    Match.exhaustive,
  );
};

export const reorderAboveExpandedBlock = ({
  layout,
  fromIndex,
  toIndex,
  expandedBlockId,
  noColumns,
  expandedMinRows,
  maxUsableRows,
}: {
  layout: Layout;
  fromIndex: number;
  toIndex: number;
  expandedBlockId: string;
  noColumns: number;
  expandedMinRows: number;
  maxUsableRows: number;
}): Layout | undefined => {
  const expandedIndex = sortLayoutByReadingOrder(layout).findIndex((l) => l.i === expandedBlockId);
  if (expandedIndex < 0) return undefined;
  const clampedToIndex = Math.min(toIndex, expandedIndex - 1);
  const repacked = reorderBlockWithinPage({ layout, fromIndex, toIndex: clampedToIndex, noColumns });
  const newExpanded = repacked.find((l) => l.i === expandedBlockId);
  if (!newExpanded || newExpanded.y + expandedMinRows > maxUsableRows) return undefined;
  return repacked;
};

export const reorderContinuationUserBlocks = ({
  userBlocks,
  fromIndex,
  toIndex,
  noColumns,
  topOffset,
  pageRows,
}: {
  userBlocks: Layout;
  fromIndex: number;
  toIndex: number;
  noColumns: number;
  topOffset: number;
  pageRows: number;
}): LayoutItem[] | undefined => {
  const reordered = [...userBlocks];
  const [moved] = reordered.splice(fromIndex, 1);
  if (!moved) return undefined;
  reordered.splice(toIndex, 0, moved);
  const packed = packLayoutByReadingOrder({ items: reordered, noColumns }).map((item) => ({
    ...item,
    y: item.y + topOffset,
  }));
  const occupiedBottom = packed.reduce((max, item) => Math.max(max, item.y + item.h), topOffset);
  return occupiedBottom > pageRows ? undefined : packed;
};
